import { useCallback, useMemo, useRef, useState } from "react";

import { FormProvider } from "react-hook-form";

import { useAuthContext } from "@/components/AuthProvider";
import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import CustomerTabs from "@/components/CustomerTabs";
import DataLoader from "@/components/DataLoader";
import { DutchTestQuestionsFieldIfFemale } from "@/components/DutchTestQuestions";
import Grid from "@/components/Grid";
import { ExternalTextLink } from "@/components/Links";
import { theme } from "@/core/theme";
import { PATIENT_PROFILE_FIELDS } from "@/graphql/accounts";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import useDocTitle from "@/hooks/use-doc-title";
import CrossIcon from "@/images/cross.svg?react";
import ToggleSwitch from "@/tpo/ToggleSwitch";
import ButtonV2 from "@/v2/Buttons";
import { gql, useMutation } from "@apollo/client";
import styled from "styled-components";

import Asterisk from "../../../tpo/Asterisk";
import Badge from "../../../tpo/Badge";
import { PanelBoxV2 } from "../../../tpo/Boxes";
import ChevronComponent from "../../../tpo/Chevron";
import ControlledCheckboxGroup from "../../../tpo/ControlledCheckboxGroup";
import ControlledFormField from "../../../tpo/ControlledFormField";
import ControlledRadioGroup from "../../../tpo/ControlledRadioGroup";
import DateInput from "../../../tpo/DateInput";
import EditFormButtons from "../../../tpo/EditFormButtons";
import FloatingLabelInput from "../../../tpo/FloatingLabelInput";
import FormControl from "../../../tpo/FormControl";
import Group from "../../../tpo/Group";
import Spacer from "../../../tpo/Spacer";
import Stack from "../../../tpo/Stack";
import Table from "../../../tpo/Table";
import B2CHeader from "../../../tpo/header/B2CHeader";
import TwoFactorAuthSection from "../../otp/TwoFactorAuthSection";
import { ProfileHazeSection } from "../../otp/ProfileSectionPanel";
import PatientDetailsSaveConfirmation, {
  customerProfileSaveConfirmationContent
} from "../../organisations/shared/PatientDetailsSaveConfirmation";
import Modal from "@/tpo/Modal";

const FormButtonsContainer = styled(Box)`
  a,
  button {
    align-self: center;
  }
`;

const RESULTS_ACCESS_LINK_MUTATION = gql`
  mutation ResultsAccessLinkMutation($input: ResultsAccessLinkMutationInput!) {
    resultsAccessLinkMutation(input: $input) {
      resultsAccessLink {
        id
        granted
      }
      errors {
        field
        messages
      }
    }
  }
`;

const YOUR_DETAILS_MUTATION = gql`
  mutation UserYourDetailsMutation($input: UserYourDetailsMutationInput!) {
    userYourDetailsMutation(input: $input) {
      patientProfile {
        ...PatientProfileFields
      }
      errors {
        field
        messages
      }
    }
  }
  ${PATIENT_PROFILE_FIELDS}
`;

function initializeYourDetails(patientProfile) {
  return {
    firstName: patientProfile.user.firstName,
    lastName: patientProfile.user.lastName,
    height: patientProfile.height,
    weight: patientProfile.weight,
    dateOfBirth: patientProfile.dateOfBirth || "",
    gender: patientProfile.gender,
    birthControl: patientProfile.birthControl,
    postMenopausal: patientProfile.postMenopausal,
    hrt: patientProfile.hrt
  };
}

function getResultsAccessRequestName({ requesterName }) {
  return requesterName || "Unknown requester";
}

function YourDetails({ patientProfile }) {
  const submitButtonRef = useRef();
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const { methods, onSubmit, nonFieldError, loading } = useDjangoGraphqlForm({
    mutation: YOUR_DETAILS_MUTATION,
    mutationName: "userYourDetailsMutation",
    defaultValues: initializeYourDetails(patientProfile),
    transformer: input => ({
      ...input,
      dateOfBirth: input.dateOfBirth === "--" ? "" : input.dateOfBirth,
      height: input.height || "",
      weight: input.weight || ""
    }),
    handleSuccess: ({ data, api: { reset } }) => {
      setShowSaveConfirmation(false);
      reset(initializeYourDetails(data.userYourDetailsMutation.patientProfile));
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(true);
        submitButtonRef.current.setPending(false);
      }
    },
    handleFailure: () => {
      setShowSaveConfirmation(false);
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(false);
        submitButtonRef.current.setPending(false);
      }
    }
  });

  return (
    <FormProvider {...methods} submitButtonRef={submitButtonRef}>
      <form
        onSubmit={methods.handleSubmit(data => {
          if (submitButtonRef.current) {
            submitButtonRef.current.setPending(false);
          }
          setShowSaveConfirmation(true);
        })}
      >
        <PanelBoxV2 maxWidth={1020}>
          <PanelBoxV2
            maxWidth={760}
            outer={{
              pt: [30, 30, 60],
              pb: [50, 50, 80],
              bg: "white",
              borderRadius: 5,
              px: 20
            }}
          >
            <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight="130%">
              Your details
              <Asterisk />
            </Box>
            {nonFieldError && (
              <Box color="red" py={20}>
                {nonFieldError}
              </Box>
            )}
            <Spacer py={[2, 2, 20]} />
            <Stack gap={[20, 20, 40]}>
              <FormControl
                fields={
                  <>
                    <ControlledFormField
                      name="firstName"
                      label="First name"
                      Component={FloatingLabelInput}
                    />
                    <ControlledFormField
                      name="lastName"
                      label="Last name"
                      Component={FloatingLabelInput}
                    />
                  </>
                }
              />
              <FormControl
                as="fieldset"
                label={
                  <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="26px">
                    Physical details
                    <Asterisk />
                  </Box>
                }
                fields={
                  <Stack gap={20}>
                    <ControlledFormField
                      name="height"
                      label="Height (cm)"
                      Component={FloatingLabelInput}
                    />
                    <ControlledFormField
                      name="weight"
                      label="Weight (kg)"
                      Component={FloatingLabelInput}
                    />
                  </Stack>
                }
              />
              <FormControl
                label={
                  <Box as="h2" fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="36px">
                    Date of birth
                  </Box>
                }
              >
                <ControlledFormField
                  name="dateOfBirth"
                  Component={DateInput}
                  getDay={dateStr => dateStr.split("-")?.[2] || ""}
                  getMonth={dateStr => dateStr.split("-")?.[1] || ""}
                  getYear={dateStr => dateStr.split("-")?.[0] || ""}
                  constructDate={({ day, month, year }) => {
                    return `${year}-${month}-${day}`;
                  }}
                />
              </FormControl>
              <FormControl
                label={
                  <Box as="h2" fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="36px">
                    Gender
                  </Box>
                }
              >
                <ControlledRadioGroup
                  Container={Stack}
                  containerProps={{
                    gap: 20
                  }}
                  name="gender"
                  values={[
                    {
                      label: "Male",
                      value: "Male"
                    },
                    {
                      label: "Female",
                      value: "Female"
                    }
                  ]}
                />
              </FormControl>
              <DutchTestQuestionsFieldIfFemale />
            </Stack>
            {methods.formState.isDirty && (
              <>
                <Spacer py={[2, 2, 20]} />
                <FormButtonsContainer
                  display="flex"
                  flexDirection="row"
                  flexWrap="wrap"
                  gap={20}
                  justifyContent={["flex-start", "flex-start", "center"]}
                  alignItems="center"
                >
                  <EditFormButtons />
                </FormButtonsContainer>
              </>
            )}
          </PanelBoxV2>
        </PanelBoxV2>
      </form>
      <Modal
        maxWidth={1024}
        closeButton={false}
        bg="white"
        show={showSaveConfirmation}
        close={() => setShowSaveConfirmation(false)}
        mode={["fullScreen", "fullScreen", "centered"]}
        data-component-name="CustomerProfileSaveConfirmationModal"
      >
        <Box px={[20]} maxWidth={760} mx="auto">
          <PatientDetailsSaveConfirmation
            {...customerProfileSaveConfirmationContent}
            loading={loading}
            onCancel={() => setShowSaveConfirmation(false)}
            onSave={() => methods.handleSubmit(onSubmit)()}
            data-component-name="CustomerProfileSaveConfirmation"
          />
        </Box>
      </Modal>
    </FormProvider>
  );
}

const DIET_PREFERENCES_MUTATION = gql`
  mutation DietPreferencesMutation($input: DietPreferencesMutationInput!) {
    dietPreferencesMutation(input: $input) {
      patientProfile {
        id
        pk
        dietPreferences {
          id
          name
        }
      }
      errors {
        field
        messages
      }
    }
  }
`;

function initializeDietPreferences(patientProfile) {
  return {
    dietPreferences: patientProfile.dietPreferences.map(dietPreference => dietPreference.id)
  };
}

function DietPreferences({ patientProfile, dietPreferences }) {
  const submitButtonRef = useRef();

  const { methods, onSubmit, nonFieldError } = useDjangoGraphqlForm({
    mutation: DIET_PREFERENCES_MUTATION,
    mutationName: "dietPreferencesMutation",
    defaultValues: initializeDietPreferences(patientProfile),
    handleSuccess: ({ data, api: { reset } }) => {
      reset(initializeDietPreferences(data.dietPreferencesMutation.patientProfile));
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(true);
        submitButtonRef.current.setPending(false);
      }
    },
    handleFailure: () => {
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(false);
        submitButtonRef.current.setPending(false);
      }
    }
  });

  return (
    <FormProvider {...methods} submitButtonRef={submitButtonRef}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <PanelBoxV2
          maxWidth={760}
          outer={{
            pt: theme.spacing.section.pt,
            pb: theme.spacing.section.pb,
            px: [20],
            bg: "white"
          }}
          stacked
          gap={40}
        >
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight="130%">
            Diet preferences
          </Box>
          {nonFieldError && (
            <Box color="red" py={20}>
              {nonFieldError}
            </Box>
          )}

          <ControlledCheckboxGroup
            Container={Grid}
            containerProps={{
              pl: [null, 80],
              gridTemplateColumns: ["1fr", "1fr 1fr"]
            }}
            name="dietPreferences"
            values={
              dietPreferences?.map(dietPreference => ({
                label: dietPreference.name,
                value: dietPreference.id
              })) || []
            }
          />

          {methods.formState.isDirty && (
            <FormButtonsContainer
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
              gap={20}
              justifyContent={["flex-start", "flex-start", "center"]}
              alignItems="center"
            >
              <EditFormButtons />
            </FormButtonsContainer>
          )}
        </PanelBoxV2>
      </form>
    </FormProvider>
  );
}

function GrantedToggle({ id, granted, accept, reject }) {
  return (
    <Group justifyContent="flex-end">
      <ToggleSwitch
        onChange={v => {
          if (granted) {
            reject(id);
          } else {
            accept(id);
          }
        }}
        value={granted}
        offBg="haze"
        onBg="haze"
        offSwitchBg="red"
        onSwitchBg="green"
        onSwitchColor="white"
        offSwitchColor="white"
        id={`results-access-toggle:${id}`}
      />
    </Group>
  );
}

function NewAccessRequestButtons({ id, reject, accept }) {
  return (
    <Group gap={20} justifyContent="flex-end" flexWrap="wrap">
      <ButtonV2
        color="red"
        onClick={() => reject(id)}
        rightIcon={<CrossIcon fill="white" width="5px" height="5px" />}
        size={["sm", "sm", "md"]}
      >
        reject
      </ButtonV2>
      <ButtonV2
        color="green"
        onClick={() => accept(id)}
        rightIcon={<ChevronComponent />}
        size={["sm", "sm", "md"]}
      >
        approved
      </ButtonV2>
    </Group>
  );
}

function ResultAccessRequests({ patientProfile, embedded = false }) {
  const [approveResultsAccessLinkMutation] = useMutation(RESULTS_ACCESS_LINK_MUTATION);

  const accept = useCallback(
    id => {
      approveResultsAccessLinkMutation({
        variables: {
          input: {
            id,
            granted: true
          }
        }
      }).catch(e => {
        console.log("An error occured accepting a result access link", e);
      });
    },
    [approveResultsAccessLinkMutation]
  );

  const reject = useCallback(
    id => {
      approveResultsAccessLinkMutation({
        variables: {
          input: {
            id,
            granted: false
          }
        }
      }).catch(e => {
        console.log("An error occured accepting a result access link", e);
      });
    },
    [approveResultsAccessLinkMutation]
  );

  const resultAccessRequestColumns = useMemo(
    () => [
      {
        id: "approvedPractitioners",
        label: "Approved Practitioners",
        verticalAlign: "middle",
        accessor: ({ requesterName }) => {
          const fullName = getResultsAccessRequestName({ requesterName });
          return (
            <Box textDecoration="underline" fontFamily="gilroyMedium" fontSize={[16, 16, 18]}>
              {fullName}
            </Box>
          );
        }
      },
      {
        id: "type",
        label: "",
        verticalAlign: "middle",
        accessor: ({ requesterIsOrganisation, requesterName }) => {
          if (!requesterName) {
            return null;
          }

          if (requesterIsOrganisation) {
            return (
              <Badge bg="haze" withBorder>
                organisation
              </Badge>
            );
          }

          return (
            <Badge bg="haze" withBorder>
              partner
            </Badge>
          );
        }
      },
      {
        id: "access",
        label: "Access",
        accessor: ({ id, granted }) => (
          <GrantedToggle id={id} granted={granted} accept={accept} reject={reject} />
        ),
        align: "right"
      }
    ],
    [accept, reject]
  );

  // i.e. filter out those where granted is null (unknown)
  const resultAccessRequestItems = patientProfile.resultsAccessLinkSet.filter(
    link => link.granted === false || link.granted === true
  );

  const newAccessRequestColumns = useMemo(
    () => [
      {
        id: "name",
        label: "New access requests",
        verticalAlign: "middle",
        accessor: ({ requesterName }) => {
          const fullName = getResultsAccessRequestName({ requesterName });

          return (
            <Box textDecoration="underline" fontFamily="gilroyMedium" fontSize={[16, 16, 18]}>
              {fullName}
            </Box>
          );
        }
      },
      {
        id: "buttons",
        label: "",
        accessor: ({ id, granted }) => (
          <NewAccessRequestButtons id={id} reject={reject} accept={accept} />
        ),
        align: "right"
      }
    ],
    [reject, accept]
  );

  // i.e. filter out those where granted is null (unknown)
  const newAccessRequestItems = patientProfile.resultsAccessLinkSet.filter(
    link => link.granted === null
  );

  const content = (
    <PanelBoxV2
      maxWidth={760}
      outer={{
        pt: [30, 30, 60],
        pb: [50, 50, 80],
        bg: "white",
        borderRadius: 5,
        px: 20
      }}
      stacked
      gap={40}
    >
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight="130%">
          Results access requests
        </Box>
        <Spacer py={[2, 2, 20]} />
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%">
          Control access to your results below
        </Box>
      </Box>
      <Table
        emptyMessage="No approved practitioners"
        rows={resultAccessRequestItems}
        columns={resultAccessRequestColumns}
      />
      <Table
        emptyMessage="No new access requests"
        rows={newAccessRequestItems}
        columns={newAccessRequestColumns}
      />
    </PanelBoxV2>
  );

  if (embedded) {
    return <PanelBoxV2 maxWidth={1020}>{content}</PanelBoxV2>;
  }

  return (
    <PanelBoxV2
      maxWidth={1532}
      outer={{
        pt: theme.spacing.section.pt,
        pb: theme.spacing.section.pb,
        px: [20],
        bg: "haze"
      }}
    >
      <PanelBoxV2 maxWidth={1020}>{content}</PanelBoxV2>
    </PanelBoxV2>
  );
}

function YourData() {
  return (
    <PanelBoxV2
      maxWidth={760}
      outer={{
        pt: theme.spacing.section.pt,
        pb: theme.spacing.section.pb,
        px: 20
      }}
      gap={16}
      stacked
    >
      <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight={["46px"]}>
        Your data
      </Box>
      <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight={["28px"]}>
        You have the right to request access to the personal data we hold about you, and to ask us
        to delete your account. To make a request, email{" "}
        <ExternalTextLink href="mailto:info@regeneruslabs.com">
          info@regeneruslabs.com
        </ExternalTextLink>
        . We will need to verify your identity before completing your request, and we&apos;ll
        respond in line with the applicable data protection law.
      </Box>
    </PanelBoxV2>
  );
}

const INITIAL_QUERY = gql`
  query InitialQuery {
    dietPreferences {
      id
      name
    }
    patientProfile {
      ...PatientProfileFields
      resultsAccessLinkSet {
        id
        requesterName
        requesterIsOrganisation
        granted
      }
    }
  }
  ${PATIENT_PROFILE_FIELDS}
`;

export default function ProfilePage() {
  useDocTitle("Profile");
  const { otp } = useAuthContext();
  return (
    <>
      <B2CHeader />
      <CustomerPage
        jumbotronProps={{
          title: "Profile Settings"
        }}
      >
        <CustomerTabs selectedTab="/profile" />
        <DataLoader
          query={INITIAL_QUERY}
          fetchPolicy="network-only"
          nextFetchPolicy="network-only"
          render={({ patientProfile, dietPreferences }) =>
            patientProfile ? (
              <>
                <PanelBoxV2
                  maxWidth={1532}
                  outer={{
                    pt: theme.spacing.section.pt,
                    pb: theme.spacing.section.pb,
                    px: [20],
                    bg: "haze"
                  }}
                  stacked
                  gap={40}
                >
                  <YourDetails patientProfile={patientProfile} />
                </PanelBoxV2>
                <DietPreferences
                  patientProfile={patientProfile}
                  dietPreferences={dietPreferences}
                />
                <ProfileHazeSection>
                  <ResultAccessRequests patientProfile={patientProfile} embedded />
                  <TwoFactorAuthSection otp={otp} embedded />
                </ProfileHazeSection>
                <YourData />
              </>
            ) : null
          }
        />
      </CustomerPage>
    </>
  );
}
